import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import { LinkTabNavHorizontal } from '../../components';

import css from './UserNav.module.css';

/**
 * A component that renders a navigation bar for a user-specific pages.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.currentPage - The current page (e.g. 'ManageListingsPage')
 * @param {'dark' | 'cream'} [props.skin] - 'dark' is the stock skin; 'cream' is the
 *   TPH "Editorial Quiet" bar (visible on mobile too, with short tab labels)
 * @returns {JSX.Element} User navigation component
 */
const UserNav = props => {
  const { className, rootClassName, currentPage, showManageListingsLink, skin = 'dark' } = props;
  const intl = useIntl();
  const isCream = skin === 'cream';
  const classes = classNames(rootClassName || (isCream ? css.rootCream : css.root), className);

  // The cream bar stays visible on mobile, where the reference uses short
  // labels ("Listings" / "Account"); the dark skin keeps its plain labels.
  const label = (full, short) =>
    isCream && short ? (
      <>
        <span className={css.labelFull}>{full}</span>
        <span className={css.labelShort}>{short}</span>
      </>
    ) : (
      full
    );

  const manageListingsTabMaybe = showManageListingsLink
    ? [
        {
          text: label(
            <FormattedMessage id="UserNav.yourListings" />,
            <FormattedMessage id="UserNav.yourListingsShort" />
          ),
          selected: currentPage === 'ManageListingsPage',
          linkProps: {
            name: 'ManageListingsPage',
          },
        },
      ]
    : [];

  const tabs = [
    ...manageListingsTabMaybe,
    {
      // New key (§22.2): Console Microcopy shadows UserNav.profileSettings
      // with the stock "Profile settings"
      text: label(<FormattedMessage id="UserNav.publicProfileTab" />, null),
      selected: currentPage === 'ProfileSettingsPage',
      disabled: false,
      linkProps: {
        name: 'ProfileSettingsPage',
      },
    },
    {
      text: label(
        <FormattedMessage id="UserNav.accountSettings" />,
        <FormattedMessage id="UserNav.accountSettingsShort" />
      ),
      selected: ACCOUNT_SETTINGS_PAGES.includes(currentPage),
      disabled: false,
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
  ];

  return (
    <LinkTabNavHorizontal
      className={classes}
      tabRootClassName={isCream ? css.tabCream : css.tab}
      tabs={tabs}
      skin={skin}
      ariaLabel={intl.formatMessage({ id: 'UserNav.screenreader.userNav' })}
    />
  );
};

export default UserNav;
